import { FC, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import { t } from 'i18next';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceChangeDrawerProps } from './PriceChangeDrawerProps.types';
import { useSTKContext } from '@/context/context';
import styles from './PriceChangeConfirmation.module.css';

export const PriceChangeConfirmation: FC<PriceChangeDrawerProps> = ({
  isOpen,
  onClose,
  onCancel,
  storeProducts,
  afterSave,
  newPrice,
}) => {
  const { storesAndGroups } = useSTKContext();

  // Get all store group names from the user's selection (same as ProductsTable)
  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    // Extract store group names based on origin
    if (storesAndGroups.origin === 'tab-store') {
      // When selecting individual stores, get unique store group names
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      // When selecting store groups, get the group names
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  // Get store counts from context (same as ProductsTable)
  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  const handleSubmit = () => {
    afterSave();
    onClose();
  };

  const handleCloseDrawer = () => {
    // X button should close entire drawer flow if onCancel is provided
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    // Cancel button should go back to price input drawer
    onClose();
  };

  // Format price with dollar sign
  const formattedNewPrice = `$${parseFloat(newPrice || '0').toFixed(2)}`;

  return (
    <SwipeableDrawer open={isOpen} onClose={handleCloseDrawer} onOpen={() => undefined} anchor="bottom">
      <div className={styles.container}>
        <header>
          <div>
            <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
          </div>
          <button type="button" onClick={handleCloseDrawer}>
            <X data-testid="x-icon" size={24} color="var(--black-900)" />
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.confirmationContent}>
            <h2 className={styles.confirmationTitle}>{t('editMenuPrice.submitMenuChanges')}</h2>

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.productHeader}>{t('editMenuPrice.product')}</th>
                    {allStoreGroupNames.map((storeGroupName) => {
                      const { count, total } = getStoreGroupsCount(storeGroupName);
                      return (
                        <th key={storeGroupName} className={styles.priceHeader}>
                          <div className={styles.headerWithCount}>
                            <div className={styles.headerName}>{storeGroupName}</div>
                            <div className={styles.headerCount}>
                              {count} / {total}
                            </div>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {storeProducts.map((product, index) => (
                    <tr key={product.variantId || index}>
                      <td className={styles.productCell}>
                        <div className={styles.productColumnContent}>
                          <div className={styles.productName}>{product.productName}</div>
                          <div className={styles.productDescription}>{product.productDescription}</div>
                        </div>
                      </td>
                      {allStoreGroupNames.map((storeGroupName) => {
                        // Always show the new price for all selected store groups
                        // This confirmation shows what price will be applied
                        return (
                          <td key={storeGroupName} className={styles.priceCell}>
                            {formattedNewPrice}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.alertContainer}>
              <CalendarAlert requestedVerbiage={true} />
            </div>

            <div className={styles.buttonContainer}>
              <Button
                className={styles.cancelButton}
                onClick={handleCancel}
                variant={ButtonVariants.Secondary}
                type="button"
              >
                {t('editMenuPrice.cancel')}
              </Button>
              <Button
                className={styles.submitButton}
                onClick={handleSubmit}
                variant={ButtonVariants.Primary}
                type="button"
              >
                {t('editMenuPrice.submitChanges')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};
